import phone from "phone";

/**
 * Payee Management POC (REQS-50): helpers shared by the sender's saved and recent recipients —
 * the "Send again" strip, the People screen, the "To" field and the success-screen save prompt.
 */

export const digitsOf = (value) => (value || "").replace(/\D/g, "").slice(-10);

export const formatRecipient = ({ type, value }) => {
  if (type !== "phone") {
    return value;
  }
  const d = digitsOf(value);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : value;
};

/** Short label for the strip: nickname, else a trimmed phone/email. */
export const shortLabel = (entry) => {
  if (entry.nickname) {
    return entry.nickname;
  }
  if (entry.type === "phone") {
    const d = digitsOf(entry.value);
    return d.length === 10 ? `…${d.slice(6)}` : entry.value;
  }
  return entry.value.split("@")[0];
};

export const sameRecipient = (a, typed) => {
  const t = (typed || "").trim().toLowerCase();
  if (!t) {
    return false;
  }
  return a.type === "email"
    ? a.value.toLowerCase() === t
    : digitsOf(t).length === 10 && digitsOf(a.value) === digitsOf(t);
};

export const matchesQuery = (entry, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) {
    return true;
  }
  const qDigits = q.replace(/\D/g, "");
  return (
    (entry.nickname || "").toLowerCase().includes(q) ||
    entry.value.toLowerCase().includes(q) ||
    (qDigits.length >= 3 && entry.value.replace(/\D/g, "").includes(qDigits))
  );
};

export const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

export const sentAgo = (iso) => {
  if (!iso) {
    return "";
  }
  const when = new Date(`${iso}Z`);
  const days = Math.floor((Date.now() - when.getTime()) / 86400000);
  if (days <= 0) return "Sent today";
  if (days === 1) return "Sent yesterday";
  if (days < 30) return `Sent ${days} days ago`;
  return `Sent ${when.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
};

/** A typed phone/email in the shape transfers store it (phones as E.164). */
export const toRecipient = (raw) => {
  const value = (raw || "").trim();
  if (value.includes("@")) {
    return { type: "email", value: value.toLowerCase() };
  }
  return { type: "phone", value: phone(value, { country: "USA" }).phoneNumber || value };
};

export const icons = {
  phone: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
      <path d="M11 18.5h2" />
    </svg>
  ),
  email: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m4 7 8 6 8-6" />
    </svg>
  ),
  star: (filled, size = 20) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
      <path d="m12 3.2 2.7 5.5 6 .9-4.35 4.25 1.03 6-5.38-2.83-5.38 2.83 1.03-6L3.3 9.6l6-.9L12 3.2Z" />
    </svg>
  ),
  search: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.2-4.2" />
    </svg>
  ),
};

/** Avatar for a person: initials on accent for favorites, a phone/email glyph otherwise. */
export const Avatar = ({ entry, large = false }) => {
  const cls = `np-avatar${large ? " np-avatar-lg" : ""}`;
  if (entry.nickname) {
    return (
      <span className={`${cls} np-avatar-accent np-avatar-initials`}>
        {initials(entry.nickname) || icons[entry.type]}
      </span>
    );
  }
  return <span className={cls}>{icons[entry.type] || icons.email}</span>;
};
